import { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Container,
  Divider, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Tooltip,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';

// ---------------------------------------------------------------------------
// Campaign designer — deterministic per-plot design (v0.4 model, 2026-09-29).
// Replaces the old percentage-based tier design (P1/P2/P3): a real campaign is
// a per-plot test MATRIX — every plot has a ROLE (project/control) and a
// concrete test set; TOC + BD are always on (ESM pairs them); texture/pH/EC run
// on a chosen subset; QA duplicates are named plots with blind lab codes.
// Old tier-shaped drafts/artifacts still load (migrated on read).
// ---------------------------------------------------------------------------

// StudioAmbiente rate card, quote MG/082L/2026 (net of IVA) — editable in the UI.
const DEFAULT_RATES = {
  toc: 50, bd: 50, texture: 50, ph: 20, ec: 20,
  fieldDayBd: 1000,      // BD sampling with their cylinder kit, per day
  fieldDayAssist: 500,   // logistics / sampling assistance, per day
};

const DEFAULT_RECIPE = {
  depths: ['0-30', '30-60'],
  subcores: 10,
  annulus: '1-3 m (inner 1 m reserved for BD cylinders)',
  bdMethod: 'Laboratory known-volume cylinder kit, one cylinder per depth',
};

// The decided t0 design (protocol v0.4): applied as defaults when the loaded
// point set matches the canonical names — first act is reviewing, not re-entering.
const V04_SUBSET = new Set(['AB-02', 'AB-04', 'AB-07', 'CD-02', 'CD-04', 'CD-07', 'QUA-02', 'QUB-02']);
const V04_DUPLICATES = new Set(['AB-03', 'CD-03']);

function roleOf(stratum, name) {
  const s = `${stratum || ''} ${name || ''}`.toLowerCase();
  return /control|qu-a|qu-b|^qu|\bqua|\bqub/.test(s) ? 'control' : 'project';
}

function dupCodeFor(i) { return `QC-${String(i + 1).padStart(2, '0')}`; }

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

// Old drafts (tier model) → matrix model. salinity covered pH+EC in the old card.
function migrateOldRows(rows) {
  return (rows || []).map((r, idx) => ({
    id: idx + 1,
    name: r.name || `SP-${idx + 1}`,
    stratum: r.stratum || 'Unassigned',
    lat: r.lat, lng: r.lng,
    role: roleOf(r.stratum, r.name),
    texture: !!r.texture, ph: !!r.salinity || !!r.ph, ec: !!r.salinity || !!r.ec,
    duplicate: !!r.duplicate, dupCode: r.dupCode || '',
  }));
}

function computeTotals(points, recipe, rates, fieldDays) {
  const depths = recipe.depths.length;
  const nProj = points.filter((p) => p.role === 'project').length;
  const nCtrl = points.length - nProj;
  const dupPlots = points.filter((p) => p.duplicate).length;
  const composite = points.length * depths;
  const dupSamples = dupPlots * depths;                 // TOC only
  const toc = composite + dupSamples;
  const bd = points.length * depths;
  const texture = points.filter((p) => p.texture).length * depths;
  const ph = points.filter((p) => p.ph).length * depths;
  const ec = points.filter((p) => p.ec).length * depths;
  const containers = points.length * depths * 2 + dupSamples; // cylinders + bags per depth, + dup bags
  const analyses = toc * rates.toc + bd * rates.bd + texture * rates.texture + ph * rates.ph + ec * rates.ec;
  const field = fieldDays * (Number(rates.fieldDayBd) + Number(rates.fieldDayAssist));
  return { nProj, nCtrl, dupPlots, dupSamples, composite, toc, bd, texture, ph, ec, containers,
    analyses, field, total: analyses + field,
    dupPct: composite ? Math.round((dupSamples / composite) * 1000) / 10 : 0 };
}

export default function SamplingPage() {
  const { selectedProject, selectedSite } = useProject();
  const projectId = selectedProject?.code;
  const [searchParams] = useSearchParams();

  const [points, setPoints] = useState([]);
  const [recipe, setRecipe] = useState(DEFAULT_RECIPE);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [fieldDays, setFieldDays] = useState(2);
  const [campaignName, setCampaignName] = useState('t0 baseline campaign');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [libraryArtifacts, setLibraryArtifacts] = useState([]);
  const [libSel, setLibSel] = useState('');
  const [subsetPct, setSubsetPct] = useState(30);
  const [dupPct, setDupPct] = useState(8);

  // Stage-4 point artifacts for the Load selector
  useEffect(() => {
    if (!projectId) return;
    fetch(`/v1/stratification/artifacts?project=${encodeURIComponent(projectId)}&tool=sampling-points`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setLibraryArtifacts(Array.isArray(rows) ? rows.filter((a) => a.subtype === 'points') : []))
      .catch(() => setLibraryArtifacts([]));
  }, [projectId]);

  const loadFromLibrary = useCallback(async (artifactId) => {
    try {
      setLoading(true);
      const res = await fetch(`/v1/stratification/artifacts/${artifactId}`);
      const art = await res.json();
      const payload = typeof art.payload_json === 'string' ? JSON.parse(art.payload_json) : art.payload_json;
      const pts = (payload?.features || []).filter((f) => f.geometry?.type === 'Point');
      if (!pts.length) return;
      const names = new Set(pts.map((f) => f.properties?.name));
      const isCanonical = [...V04_DUPLICATES].every((n) => names.has(n));
      let dupIdx = 0;
      setPoints(pts.map((f, idx) => {
        const name = f.properties?.name || f.properties?.label || `SP-${idx + 1}`;
        const stratum = f.properties?.stratum !== undefined ? String(f.properties.stratum) : '';
        const duplicate = isCanonical && V04_DUPLICATES.has(name);
        return {
          id: idx + 1, name, stratum,
          lat: Number(f.geometry.coordinates[1]).toFixed(6),
          lng: Number(f.geometry.coordinates[0]).toFixed(6),
          role: roleOf(stratum, name),
          texture: isCanonical && V04_SUBSET.has(name),
          ph: isCanonical && V04_SUBSET.has(name),
          ec: isCanonical && V04_SUBSET.has(name),
          duplicate, dupCode: duplicate ? dupCodeFor(dupIdx++) : '',
        };
      }));
      setSavedMsg(isCanonical ? 'Loaded with the decided v0.4 defaults (subset + duplicates pre-ticked).' : 'Loaded — assign subset and duplicates.');
    } finally { setLoading(false); }
  }, []);

  // Initial load: ?artifactId= (stage-5) → workspace draft → empty
  useEffect(() => {
    if (!projectId) return;
    const artifactId = searchParams.get('artifactId');
    (async () => {
      setLoading(true);
      try {
        if (artifactId) {
          const res = await fetch(`/v1/stratification/artifacts/${artifactId}`);
          const art = await res.json();
          const payload = typeof art.payload_json === 'string' ? JSON.parse(art.payload_json) : art.payload_json;
          if (payload?.points) {           // new shape
            setPoints(payload.points);
            if (payload.recipe) setRecipe(payload.recipe);
            if (payload.rates) setRates(payload.rates);
            if (payload.fieldDays) setFieldDays(payload.fieldDays);
            if (payload.campaignName) setCampaignName(payload.campaignName);
          } else if (payload?.samplingData) { // old tier shape — migrate
            setPoints(migrateOldRows(payload.samplingData));
            if (payload.campaignName) setCampaignName(payload.campaignName);
            setSavedMsg('Loaded a tier-era campaign — migrated to the matrix view.');
          }
          return;
        }
        const response = await fetch(`/v1/project-workspace/card?projectId=${encodeURIComponent(projectId)}&cardType=sampling`);
        const result = await response.json();
        if (result.ok && result.state) {
          const st = result.state;
          if (st.points) {                 // new draft shape
            setPoints(st.points);
            if (st.recipe) setRecipe(st.recipe);
            if (st.rates) setRates(st.rates);
            if (st.fieldDays) setFieldDays(st.fieldDays);
            if (st.campaignName) setCampaignName(st.campaignName);
          } else if (st.samplingData?.length) {
            setPoints(migrateOldRows(st.samplingData));
            if (st.campaignName) setCampaignName(st.campaignName);
            setSavedMsg('Migrated your saved tier-era draft to the matrix view.');
          }
        }
      } catch (e) { console.error('load failed', e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Debounced draft autosave (workspace card, new shape)
  useEffect(() => {
    if (!projectId || points.length === 0) return undefined;
    const timer = setTimeout(async () => {
      try {
        setSaving(true); setSaveError('');
        const response = await fetch('/v1/project-workspace/card', {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId, cardType: 'sampling',
            state: { v: 2, points, recipe, rates, fieldDays, campaignName, savedAt: new Date().toISOString() } }),
        });
        const result = await response.json();
        if (!result.ok) setSaveError('Draft autosave failed');
      } catch (e) { setSaveError('Draft autosave failed'); }
      finally { setSaving(false); }
    }, 1000);
    return () => clearTimeout(timer);
  }, [points, recipe, rates, fieldDays, campaignName, projectId]);

  const totals = useMemo(() => computeTotals(points, recipe, rates, fieldDays), [points, recipe, rates, fieldDays]);

  // Compliance checks (live)
  const ctrlStrata = useMemo(() => {
    const m = {};
    points.filter((p) => p.role === 'control').forEach((p) => { m[p.stratum || '?'] = (m[p.stratum || '?'] || 0) + 1; });
    return m;
  }, [points]);
  const ctrlOk = Object.keys(ctrlStrata).length > 0 && Object.values(ctrlStrata).every((n) => n >= 3);
  const dupOk = totals.dupPct >= 5;

  const setPoint = (id, patch) => setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  // Toggling a duplicate re-issues ALL blind codes in row order — codes stay
  // dense (QC-01, QC-02, …) and never leak the plot identity.
  const toggleDup = (id) => setPoints((prev) => {
    let dupIdx = 0;
    return prev.map((p) => {
      const duplicate = p.id === id ? !p.duplicate : p.duplicate;
      return { ...p, duplicate, dupCode: duplicate ? dupCodeFor(dupIdx++) : '' };
    });
  });

  // % auto-fill: percent is the INPUT, named plots are the OUTPUT — picks are
  // evenly spaced within each stratum (min 1 per stratum when pct > 0).
  const pickPerStratum = (prev, pct) => {
    const byStratum = {};
    prev.forEach((p) => { (byStratum[p.stratum] = byStratum[p.stratum] || []).push(p); });
    const chosen = new Set();
    Object.values(byStratum).forEach((group) => {
      const n = pct <= 0 ? 0 : Math.min(group.length, Math.max(1, Math.round((pct / 100) * group.length)));
      for (let i = 0; i < n; i += 1) {
        const idx = Math.min(group.length - 1, Math.max(0, Math.round(((i + 0.5) * group.length) / n) - 1));
        chosen.add(group[idx].id);
      }
    });
    return chosen;
  };

  const fillSubset = () => setPoints((prev) => {
    const chosen = pickPerStratum(prev, subsetPct);
    return prev.map((p) => ({ ...p, texture: chosen.has(p.id), ph: chosen.has(p.id), ec: chosen.has(p.id) }));
  });
  const clearSubset = () => setPoints((prev) => prev.map((p) => ({ ...p, texture: false, ph: false, ec: false })));

  // Duplicates: pick ceil(pct% of plots), project strata first (one each,
  // round-robin), then controls — codes re-issued densely. 5% is the floor.
  const fillDuplicates = () => setPoints((prev) => {
    const target = dupPct <= 0 ? 0 : Math.max(1, Math.ceil((dupPct / 100) * prev.length));
    const byStratum = {};
    prev.forEach((p) => { (byStratum[p.stratum] = byStratum[p.stratum] || []).push(p); });
    const groups = Object.values(byStratum).sort((a, b) => (a[0].role === 'project' ? -1 : 1) - (b[0].role === 'project' ? -1 : 1));
    const chosen = new Set();
    let round = 0;
    while (chosen.size < target && round < 20) {
      groups.forEach((g) => {
        if (chosen.size >= target) return;
        const inGroup = g.filter((p) => !chosen.has(p.id));
        if (inGroup.length) chosen.add(inGroup[Math.floor(inGroup.length / 2)].id);
      });
      round += 1;
    }
    let dupIdx = 0;
    return prev.map((p) => {
      const duplicate = chosen.has(p.id);
      return { ...p, duplicate, dupCode: duplicate ? dupCodeFor(dupIdx++) : '' };
    });
  });
  const clearDuplicates = () => setPoints((prev) => prev.map((p) => ({ ...p, duplicate: false, dupCode: '' })));

  const saveToLibrary = async () => {
    if (!projectId || points.length === 0) return;
    try {
      const body = {
        projectId, siteId: selectedSite?.code || null,
        tool: 'sampling-campaign', subtype: 'campaign', title: campaignName,
        params: { designVersion: 'matrix-v1', plotCount: points.length, projectPlots: totals.nProj, controlPlots: totals.nCtrl },
        payload: {
          campaignName, designVersion: 'matrix-v1',
          // samplingData keeps the {name,lat,lng} shape field-records consumes
          samplingData: points.map((p) => ({ name: p.name, lat: p.lat, lng: p.lng })),
          points, recipe, rates, fieldDays, totals,
          duplicateKey: points.filter((p) => p.duplicate).map((p) => ({ code: p.dupCode, plot: p.name })),
        },
      };
      const r = await fetch('/v1/stratification/artifacts', { method: 'POST', headers: bearerHeaders(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (r.status === 401) { setSaveError('Session expired — sign in again, then save.'); return; }
      if (!r.ok || d.ok === false) { setSaveError(d.message || 'Save failed'); return; }
      setSavedMsg(`"${campaignName}" saved to the Library (stage 5).`);
    } catch (e) { setSaveError(String(e?.message || e)); }
  };

  const eur = (n) => `€${Number(n).toLocaleString('en-GB')}`;

  return (
    <>
      <Helmet><title> Sampling Campaign | TERRRA </title></Helmet>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">Sampling campaign</Typography>
            <Typography variant="body2" color="text.secondary">
              Per-plot design: every plot gets TOC + bulk density at every depth; choose the characterisation subset and the QA duplicates.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Campaign name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} sx={{ minWidth: 220 }} />
            <Select size="small" displayEmpty value={libSel} onChange={(e) => { setLibSel(e.target.value); if (e.target.value) loadFromLibrary(e.target.value); }} sx={{ minWidth: 260 }}>
              <MenuItem value=""><em>Load points from Library…</em></MenuItem>
              {libraryArtifacts.map((a) => <MenuItem key={a.id} value={a.id}>{a.title || `Points #${a.id}`}</MenuItem>)}
            </Select>
            <Button variant="contained" startIcon={<Iconify icon="eva:save-fill" />} disabled={points.length === 0} onClick={saveToLibrary}>
              Save to Library
            </Button>
          </Stack>
        </Stack>

        {saveError && <Alert severity="error" onClose={() => setSaveError('')} sx={{ mb: 2 }}>{saveError}</Alert>}
        {savedMsg && <Alert severity="success" onClose={() => setSavedMsg('')} sx={{ mb: 2 }}>{savedMsg}</Alert>}
        {saving && <Typography variant="caption" color="text.secondary">saving draft…</Typography>}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
        ) : (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              {/* Campaign recipe */}
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Campaign recipe</Typography>
                  <Stack spacing={1.5}>
                    <TextField size="small" label="Depth increments (comma-separated, cm)" value={recipe.depths.join(', ')}
                      onChange={(e) => setRecipe({ ...recipe, depths: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                    <Stack direction="row" spacing={1.5}>
                      <TextField size="small" type="number" label="Subcores per composite" value={recipe.subcores}
                        onChange={(e) => setRecipe({ ...recipe, subcores: Number(e.target.value) || 0 })} sx={{ width: 190 }} />
                      <TextField size="small" label="Subcore pattern" value={recipe.annulus} fullWidth
                        onChange={(e) => setRecipe({ ...recipe, annulus: e.target.value })} />
                    </Stack>
                    <TextField size="small" label="Bulk-density method" value={recipe.bdMethod}
                      onChange={(e) => setRecipe({ ...recipe, bdMethod: e.target.value })} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <TextField size="small" type="number" label="Field days" value={fieldDays}
                        onChange={(e) => setFieldDays(Number(e.target.value) || 1)} sx={{ width: 120 }} />
                      <Typography variant="caption" color="text.secondary">
                        Rates (editable): TOC €{rates.toc} · BD €{rates.bd} · texture €{rates.texture} · pH €{rates.ph} · EC €{rates.ec} · field day €{Number(rates.fieldDayBd) + Number(rates.fieldDayAssist)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {Object.entries({ toc: 'TOC', bd: 'BD', texture: 'Texture', ph: 'pH', ec: 'EC' }).map(([k, label]) => (
                        <TextField key={k} size="small" type="number" label={label} value={rates[k]} sx={{ width: 90 }}
                          onChange={(e) => setRates({ ...rates, [k]: Number(e.target.value) || 0 })} />
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Totals + compliance */}
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Totals</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip size="small" label={`${points.length} plots`} />
                    <Chip size="small" color="success" variant="outlined" label={`${totals.nProj} project`} />
                    <Chip size="small" variant="outlined" label={`${totals.nCtrl} control`} />
                    <Chip size="small" label={`${totals.containers} containers`} />
                  </Stack>
                  <Table size="small">
                    <TableBody>
                      <TableRow><TableCell>TOC (incl. {totals.dupSamples} duplicate)</TableCell><TableCell align="right">{totals.toc}</TableCell><TableCell align="right">{eur(totals.toc * rates.toc)}</TableCell></TableRow>
                      <TableRow><TableCell>Bulk density + water content</TableCell><TableCell align="right">{totals.bd}</TableCell><TableCell align="right">{eur(totals.bd * rates.bd)}</TableCell></TableRow>
                      <TableRow><TableCell>Texture</TableCell><TableCell align="right">{totals.texture}</TableCell><TableCell align="right">{eur(totals.texture * rates.texture)}</TableCell></TableRow>
                      <TableRow><TableCell>pH / EC</TableCell><TableCell align="right">{totals.ph} / {totals.ec}</TableCell><TableCell align="right">{eur(totals.ph * rates.ph + totals.ec * rates.ec)}</TableCell></TableRow>
                      <TableRow><TableCell>Field services × {fieldDays} day{fieldDays > 1 ? 's' : ''}</TableCell><TableCell /><TableCell align="right">{eur(totals.field)}</TableCell></TableRow>
                      <TableRow><TableCell sx={{ fontWeight: 700 }}>Campaign total (net of IVA)</TableCell><TableCell /><TableCell align="right" sx={{ fontWeight: 700 }}>{eur(totals.total)}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Field duplicates must be ≥5% of composite samples">
                      <Chip size="small" color={dupOk ? 'success' : 'error'} label={`duplicates ${totals.dupPct}%`} />
                    </Tooltip>
                    <Tooltip title="At least 3 control plots per control unit">
                      <Chip size="small" color={ctrlOk ? 'success' : 'error'}
                        label={`controls: ${Object.entries(ctrlStrata).map(([s, n]) => `${s.split('·')[0].trim()} ${n}`).join(' · ') || 'none'}`} />
                    </Tooltip>
                    <Chip size="small" color="success" label="BD paired with every TOC" />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>

            {/* Per-plot matrix */}
            {points.length > 0 && (
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Typography variant="h6">Per-plot matrix</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <TextField size="small" type="number" label="Subset %" value={subsetPct}
                        onChange={(e) => setSubsetPct(Number(e.target.value) || 0)} sx={{ width: 110 }} />
                      <Button size="small" variant="outlined" onClick={fillSubset}>Fill texture/pH/EC</Button>
                      <Button size="small" onClick={clearSubset}>Clear</Button>
                      <Divider orientation="vertical" flexItem />
                      <TextField size="small" type="number" label="Duplicates %" value={dupPct}
                        onChange={(e) => setDupPct(Number(e.target.value) || 0)} sx={{ width: 130 }} />
                      <Button size="small" variant="outlined" onClick={fillDuplicates}>Fill duplicates</Button>
                      <Button size="small" onClick={clearDuplicates}>Clear</Button>
                    </Stack>
                  </Stack>
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Plot</TableCell>
                          <TableCell>Stratum</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell align="center">TOC</TableCell>
                          <TableCell align="center">BD</TableCell>
                          <TableCell align="center">Texture</TableCell>
                          <TableCell align="center">pH</TableCell>
                          <TableCell align="center">EC</TableCell>
                          <TableCell align="center">QA duplicate</TableCell>
                          <TableCell>Blind code</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {points.map((p) => (
                          <TableRow key={p.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{p.stratum}</TableCell>
                            <TableCell>
                              <Chip size="small" clickable
                                color={p.role === 'project' ? 'success' : 'default'}
                                variant={p.role === 'project' ? 'filled' : 'outlined'}
                                label={p.role}
                                onClick={() => setPoint(p.id, { role: p.role === 'project' ? 'control' : 'project' })} />
                            </TableCell>
                            <TableCell align="center"><Checkbox size="small" checked disabled /></TableCell>
                            <TableCell align="center"><Checkbox size="small" checked disabled /></TableCell>
                            <TableCell align="center"><Checkbox size="small" checked={p.texture} onChange={() => setPoint(p.id, { texture: !p.texture })} /></TableCell>
                            <TableCell align="center"><Checkbox size="small" checked={p.ph} onChange={() => setPoint(p.id, { ph: !p.ph })} /></TableCell>
                            <TableCell align="center"><Checkbox size="small" checked={p.ec} onChange={() => setPoint(p.id, { ec: !p.ec })} /></TableCell>
                            <TableCell align="center"><Checkbox size="small" checked={p.duplicate} onChange={() => toggleDup(p.id)} /></TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{p.dupCode}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    TOC and bulk density are fixed on every plot and depth (equivalent-soil-mass pairing). Duplicates are a full
                    independent repeat of the composite; blind codes go on the lab-facing labels, the code↔plot key is stored in
                    the saved campaign. Controls are never part of the credited area.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {points.length === 0 && (
              <Alert severity="info">Load a points layer from the Library to start designing — the current canonical set pre-loads the decided v0.4 subset and duplicates.</Alert>
            )}
          </>
        )}
      </Container>
    </>
  );
}
